import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ExceptionService, PaginatedList, UtilsService } from '../../../xform-compat';
import { RmsBasicCrudService } from '../../components/rms-basic-crud.service';
import { CfvCalculation } from '../../../shared/models/company/cfv-calculation.model';
import { CfvAsset } from '../../../shared/models/company/cfv-asset.model';
import { CfvCommercialStage } from '../../../shared/models/company/cfv-commercial-stage.model';
import { CfvCalculationNote } from '../../../shared/models/company/cfv-calculation-note.model';
import { CfvCalculationAudit } from '../../../shared/models/company/cfv-calculation-audit.model';

/** The one status the calculator recovers from rather than reports. Shared with the state service,
 *  which is the half that does the recovering. */
export const CONFLICT = 409;

/** Body of the globals PUT: the calculation-level inputs, and nothing else. */
export interface CfvCalculationGlobals {
  timeDiscountRate: number;
  dilution: number;
  isCommercialStage: boolean;
}

/** One entry in the priorities PUT, which takes the complete ordered set rather than a delta. */
export interface CfvAssetPriority {
  id: number;
  priority: number;
}

/**
 * Stateless HTTP access to a company's fair value calculation — one method per endpoint, no page state.
 *
 * State lives in `CfvCalculatorStateService`, which is provided on the calculator component so it is created
 * and destroyed with the page. This service used to hold the current calculation and its derived totals while
 * being `providedIn: 'root'`, which meant navigating from one company's calculator straight to another's
 * rendered the previous company's assets until the request resolved.
 *
 * Every mutating endpoint returns the complete, freshly recalculated calculation, so callers replace their
 * state wholesale instead of reconciling a partial response.
 */
@Injectable({
  providedIn: 'root'
})
export class CfvDataService extends RmsBasicCrudService<CfvCalculation> {

  constructor(
    http: HttpClient,
    utilsService: UtilsService,
    exceptionService: ExceptionService
  ) {
    super(http, utilsService, exceptionService);
  }

  // --- the calculation itself -------------------------------------------------------------------------

  /**
   * Reads the calculation. Resolves to null when the company has no active one: the server answers 204, and
   * an empty body arrives here as null. The caller renders the empty calculator and defers creation.
   */
  public get(companyId: number): Observable<CfvCalculation> {
    return this.getHttp().get<CfvCalculation>(this.base(companyId))
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  /**
   * Creates the calculation.
   *
   * A `409 CFV_CALCULATION_ALREADY_EXISTS` is re-thrown unwrapped, because the caller recovers from it by
   * re-reading rather than surfacing it. Everything else goes through the shared handler as usual. This is
   * deliberate and load-bearing: `ExceptionService.catchBadResponse` never re-emits the `HttpErrorResponse`
   * — every branch of it returns `throwError(<string>)` — so a `status` check downstream of it can never
   * match, and the recovery would silently degrade into a "failed to save" banner.
   */
  public create(companyId: number): Observable<CfvCalculation> {
    return this.getHttp().post<CfvCalculation>(this.base(companyId), {})
      .pipe(catchError(ex => ex && ex.status === CONFLICT
        ? throwError(ex)
        : this.getExceptions().catchBadResponse(ex)));
  }

  public updateGlobals(companyId: number, globals: CfvCalculationGlobals): Observable<CfvCalculation> {
    return this.getHttp().put<CfvCalculation>(this.base(companyId), globals)
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  public deleteCalculation(companyId: number): Observable<void> {
    return this.getHttp().delete<void>(this.base(companyId))
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  // --- assets -----------------------------------------------------------------------------------------

  public createAsset(companyId: number, asset: CfvAsset): Observable<CfvCalculation> {
    return this.getHttp().post<CfvCalculation>(`${this.base(companyId)}/assets`, asset)
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  public updateAsset(companyId: number, asset: CfvAsset): Observable<CfvCalculation> {
    return this.getHttp().put<CfvCalculation>(`${this.base(companyId)}/assets/${asset.id}`, asset)
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  public deleteAsset(companyId: number, assetId: number): Observable<CfvCalculation> {
    return this.getHttp().delete<CfvCalculation>(`${this.base(companyId)}/assets/${assetId}`)
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  public updateAssetPriorities(companyId: number, priorities: Array<CfvAssetPriority>): Observable<CfvCalculation> {
    return this.getHttp().put<CfvCalculation>(`${this.base(companyId)}/assets/priorities`, priorities)
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  // --- commercial stage -------------------------------------------------------------------------------

  public createCommercialStage(companyId: number, stage: CfvCommercialStage): Observable<CfvCalculation> {
    return this.getHttp().post<CfvCalculation>(`${this.base(companyId)}/commercialStages`, stage)
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  public updateCommercialStage(companyId: number, stage: CfvCommercialStage): Observable<CfvCalculation> {
    return this.getHttp().put<CfvCalculation>(`${this.base(companyId)}/commercialStages/${stage.id}`, stage)
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  public deleteCommercialStage(companyId: number, stageId: number): Observable<CfvCalculation> {
    return this.getHttp().delete<CfvCalculation>(`${this.base(companyId)}/commercialStages/${stageId}`)
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  // --- note -------------------------------------------------------------------------------------------

  public upsertNote(companyId: number, note: CfvCalculationNote): Observable<CfvCalculation> {
    return this.getHttp().put<CfvCalculation>(`${this.base(companyId)}/note`, note)
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  public deleteNote(companyId: number): Observable<CfvCalculation> {
    return this.getHttp().delete<CfvCalculation>(`${this.base(companyId)}/note`)
      .pipe(catchError(ex => this.getExceptions().catchBadResponse(ex)));
  }

  // --- audit ------------------------------------------------------------------------------------------

  public getAuditTrail(companyId: number, page = 0, pagesize = 25, includeAutomated = false):
    Observable<PaginatedList<CfvCalculationAudit>> {
    return super.getData(`${this.base(companyId)}/audit`,
      { page, pagesize, includeAutomated }) as unknown as Observable<PaginatedList<CfvCalculationAudit>>;
  }

  private base(companyId: number): string {
    return `/api/rms/companies/${companyId}/fairValueCalculation`;
  }
}
